import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';

/**
 * Direct-to-S3 upload engine.
 *
 * Replaces the TUS flow (browser → Cloudflare → nginx → tusd → S3) with
 * presigned POSTs straight from the browser to S3, which measured ~29x faster
 * per stream. Flow per file:
 *   1. POST /api/galleries/:id/presigned-url  (auth, duplicate check, S3 POST policy)
 *   2. POST the file directly to S3 with the presigned form fields
 *   3. POST /api/galleries/:id/upload-complete (creates DB record, AI runs in background)
 *
 * Exposes the same context API as the old TusUploadContext so consumers
 * (UploadPanel, FloatingUploadProgress, GalleryPhotos, GallerySidebar) are unchanged.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
// Browsers cap HTTP/1.1 connections to S3 at ~6, so above that this mainly
// deepens the pipeline: files wait out their API round-trips (presigned-url,
// upload-complete) while others hold the S3 connections.
// 20-way concurrency froze real batches: every file makes TWO backend calls
// (presigned-url, upload-complete) and the shared box under load answered in
// 10s+ — 20 queued calls blew the old 30s timeout en masse (98-photo batch,
// 2026-07-17). Only the S3 PUT itself is bandwidth-bound; keep that wide via
// its own 300s timeout and pace the backend instead.
const MAX_CONCURRENT = 4;
const MAX_RETRIES = 3;
const RETRY_DELAY_BASE = 2000;

const DirectUploadContext = createContext();

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export function DirectUploadProvider({ children, onUploadComplete }) {
  const [queue, setQueue] = useState([]);
  const queueRef = useRef([]);
  const activeRef = useRef(new Set());       // uploadIds currently in flight
  const controllersRef = useRef(new Map());  // uploadId -> AbortController
  const pausedRef = useRef(new Set());       // uploadIds the user paused
  const onUploadCompleteRef = useRef(onUploadComplete);

  useEffect(() => {
    onUploadCompleteRef.current = onUploadComplete;
  }, [onUploadComplete]);

  // queueRef is the source of truth; state mirrors it for rendering
  const mutateQueue = useCallback((fn) => {
    queueRef.current = fn(queueRef.current);
    setQueue(queueRef.current);
  }, []);

  const updateItem = useCallback((uploadId, updates) => {
    mutateQueue(prev => prev.map(item =>
      item.uploadId === uploadId ? { ...item, ...updates } : item
    ));
  }, [mutateQueue]);

  const removeItem = useCallback((uploadId) => {
    mutateQueue(prev => prev.filter(item => item.uploadId !== uploadId));
  }, [mutateQueue]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || '';
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const runUpload = useCallback(async (uploadId) => {
    const item = queueRef.current.find(i => i.uploadId === uploadId);
    if (!item || item.status !== 'pending') return;
    const { file, galleryId, collectionId } = item;

    for (let attempt = 0; ; attempt++) {
      const controller = new AbortController();
      controllersRef.current.set(uploadId, controller);
      updateItem(uploadId, { status: 'uploading', progress: 0, error: null, errorType: null });

      try {
        // Step 1: presigned URL (also rejects duplicates before any bytes move)
        const presigned = await axios.post(
          `${BACKEND_URL}/api/galleries/${galleryId}/presigned-url`,
          { filename: file.name, content_type: file.type || 'image/jpeg' },
          { headers: getAuthHeaders(), signal: controller.signal, timeout: 90000 }
        );
        const { photo_id, presigned_url, s3_key, fields } = presigned.data;

        // Step 2: upload straight to S3
        const formData = new FormData();
        Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
        formData.append('file', file); // must be last (AWS requirement)

        let lastFlush = 0;
        await axios.post(presigned_url, formData, {
          signal: controller.signal,
          timeout: 300000,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const now = Date.now();
            if (now - lastFlush < 250 && e.loaded < e.total) return; // throttle re-renders
            lastFlush = now;
            updateItem(uploadId, {
              progress: Math.round((e.loaded / e.total) * 90),
              bytesUploaded: e.loaded,
              bytesTotal: e.total,
              status: 'uploading'
            });
          }
        });

        // Step 3: register with backend (DB record; AI processing runs in background)
        updateItem(uploadId, { status: 'processing', progress: 95 });
        await axios.post(
          `${BACKEND_URL}/api/galleries/${galleryId}/upload-complete`,
          { photo_id, s3_key, filename: file.name, collection_id: collectionId || null },
          { headers: getAuthHeaders(), signal: controller.signal, timeout: 180000 }
        );

        updateItem(uploadId, { status: 'completed', progress: 100 });
        if (onUploadCompleteRef.current) {
          try { onUploadCompleteRef.current(uploadId); } catch (e) { console.error(e); }
        }
        setTimeout(() => removeItem(uploadId), 3000);
        return;
      } catch (error) {
        controllersRef.current.delete(uploadId);

        // Aborted: paused keeps the item, cancelled has already removed it
        if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
          if (pausedRef.current.has(uploadId)) {
            updateItem(uploadId, { status: 'paused', progress: 0 });
          }
          return;
        }

        const status = error.response?.status;
        let detail = error.response?.data?.detail;
        if (Array.isArray(detail)) detail = detail.map(d => d.msg || d).join(', ');

        if (status === 409) {
          updateItem(uploadId, {
            status: 'duplicate',
            error: detail || `Duplicate: "${file.name}" already exists in this gallery`,
            errorType: 'duplicate'
          });
          return;
        }
        if (status === 401) {
          updateItem(uploadId, {
            status: 'failed',
            error: 'Authentication failed. Please refresh and try again.',
            errorType: 'auth'
          });
          return;
        }
        if (status === 403 || status === 404) {
          updateItem(uploadId, { status: 'failed', error: detail || 'Upload rejected', errorType: 'error' });
          return;
        }

        // Network / 5xx: retry with backoff
        if (attempt < MAX_RETRIES) {
          console.warn(`[DirectUpload] Retry ${attempt + 1}/${MAX_RETRIES} for ${file.name}:`, error.message);
          await sleep(RETRY_DELAY_BASE * Math.pow(2, attempt));
          if (pausedRef.current.has(uploadId)) {
            updateItem(uploadId, { status: 'paused', progress: 0 });
            return;
          }
          updateItem(uploadId, { status: 'pending', progress: 0 });
          continue;
        }

        console.error(`[DirectUpload] Upload failed for ${file.name}:`, error);
        updateItem(uploadId, {
          status: 'failed',
          error: detail || error.message || 'Upload failed',
          errorType: 'error'
        });
        return;
      } finally {
        controllersRef.current.delete(uploadId);
      }
    }
  }, [updateItem, removeItem]);

  const pump = useCallback(() => {
    const free = MAX_CONCURRENT - activeRef.current.size;
    if (free <= 0) return;
    const pending = queueRef.current.filter(
      item => item.status === 'pending' && !activeRef.current.has(item.uploadId)
    );
    for (const item of pending.slice(0, free)) {
      activeRef.current.add(item.uploadId);
      runUpload(item.uploadId).finally(() => {
        activeRef.current.delete(item.uploadId);
        pump();
      });
    }
  }, [runUpload]);

  // async so callers can chain .catch() like the old TUS context allowed
  const addToQueue = useCallback(async (file, galleryId, collectionId = null) => {
    const uploadId = crypto.randomUUID();
    mutateQueue(prev => [...prev, {
      uploadId,
      file,
      fileName: file.name,
      fileSize: file.size,
      galleryId,
      collectionId,
      progress: 0,
      status: 'pending',
      canResume: false
    }]);
    pump();
    return uploadId;
  }, [mutateQueue, pump]);

  const pauseUpload = useCallback((uploadId) => {
    pausedRef.current.add(uploadId);
    const controller = controllersRef.current.get(uploadId);
    if (controller) {
      controller.abort(); // runUpload's catch marks it paused
    } else {
      updateItem(uploadId, { status: 'paused', progress: 0 });
    }
  }, [updateItem]);

  const resumeUpload = useCallback((uploadId) => {
    pausedRef.current.delete(uploadId);
    updateItem(uploadId, { status: 'pending', progress: 0 });
    pump();
  }, [updateItem, pump]);

  const cancelUpload = useCallback((uploadId) => {
    pausedRef.current.delete(uploadId);
    const controller = controllersRef.current.get(uploadId);
    if (controller) controller.abort();
    removeItem(uploadId);
  }, [removeItem]);

  const clearCompleted = useCallback(() => {
    mutateQueue(prev => prev.filter(item => item.status !== 'completed'));
  }, [mutateQueue]);

  const clearAll = useCallback(() => {
    for (const controller of controllersRef.current.values()) {
      controller.abort();
    }
    controllersRef.current.clear();
    pausedRef.current.clear();
    mutateQueue(() => []);
  }, [mutateQueue]);

  // Single-pass stats (same shape as the old TUS context)
  const stats = useMemo(() => {
    let totalProgress = 0, uploading = 0, completed = 0, failed = 0, duplicate = 0, paused = 0, pending = 0;
    for (const item of queue) {
      totalProgress += item.progress || 0;
      switch (item.status) {
        case 'uploading': uploading++; break;
        case 'processing': uploading++; break;
        case 'completed': completed++; break;
        case 'failed': failed++; break;
        case 'duplicate': duplicate++; break;
        case 'paused': paused++; break;
        case 'pending': pending++; break;
        default: break;
      }
    }
    return {
      totalProgress: queue.length > 0 ? Math.round(totalProgress / queue.length) : 0,
      uploadingCount: uploading,
      completedCount: completed,
      failedCount: failed,
      duplicateCount: duplicate,
      pausedCount: paused,
      pendingCount: pending
    };
  }, [queue]);

  return (
    <DirectUploadContext.Provider value={{
      queue,
      isRecovering: false,
      addToQueue,
      pauseUpload,
      resumeUpload,
      cancelUpload,
      clearCompleted,
      clearAll,
      ...stats
    }}>
      {children}
    </DirectUploadContext.Provider>
  );
}

export function useDirectUpload() {
  const context = useContext(DirectUploadContext);
  if (!context) {
    throw new Error('useDirectUpload must be used within DirectUploadProvider');
  }
  return context;
}

export default DirectUploadContext;
